"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Support = exports.Codecs = void 0;
exports.videoSupported = videoSupported;
const tslib_1 = require("tslib");
const mediasoup_client_1 = require("mediasoup-client");
const adapter_1 = require("../../../api/adapter");
const feature_1 = require("../../../api/adapter/features/feature");
const codecs = tslib_1.__importStar(require("./codecs"));
const user_agent_1 = require("../../adapters/utils/user-agent");
var Codecs;
(function (Codecs) {
    Codecs["H264"] = "h264";
    Codecs["VP8"] = "vp8";
    Codecs["OPUS"] = "opus";
})(Codecs || (exports.Codecs = Codecs = {}));
class Support {
    constructor(logger, create) {
        this.supportChecks = async () => {
            let supported = [];
            const h264 = await codecs.hasH264Capability();
            const vp8 = await codecs.hasVP8Capability();
            const opus = await codecs.hasOpusCapability();
            if (h264) {
                supported = [Codecs.H264];
            }
            if (vp8) {
                supported = [...supported, Codecs.VP8];
            }
            if (opus) {
                supported = [...supported, Codecs.OPUS];
            }
            this.log.info("The following codecs are supported", { supported });
            return supported;
        };
        this.log = logger;
        if (create != null) {
            this.log.setMessageAggregate("create", create);
        }
        this.supportTags = {
            opus: false,
            vp8: false,
            h264: false,
        };
        const supportHash = create !== null && create !== void 0 ? create : this.buildLocalSupportHashSync();
        if (supportHash.includes("o")) {
            this.supportTags.opus = true;
        }
        if (supportHash.includes("v")) {
            this.supportTags.vp8 = true;
        }
        if (supportHash.includes("h")) {
            this.supportTags.h264 = true;
        }
        this.ready = this.buildLocalSupportHashAsync().then((s) => {
            if (s.includes("o")) {
                this.supportTags.opus = true;
            }
            if (s.includes("v")) {
                this.supportTags.vp8 = true;
            }
            if (s.includes("h")) {
                this.supportTags.h264 = true;
            }
        });
    }
    supports(tag) {
        return !!this.supportTags[tag];
    }
    get any() {
        const tagKeys = Object.keys(this.supportTags);
        return tagKeys.some((x) => this.supportTags[x]);
    }
    get audio() {
        return this.supportTags.opus;
    }
    get video() {
        return this.supportTags.vp8 || this.supportTags.h264;
    }
    get hash() {
        let hash = "";
        if (this.supportTags.opus) {
            hash += "o";
        }
        if (this.supportTags.vp8) {
            hash += "v";
        }
        if (this.supportTags.h264) {
            hash += "h";
        }
        return hash;
    }
    shared(compare) {
        if (!this.any) {
            return this;
        }
        if (!compare.any) {
            return compare;
        }
        const localHash = this.hash;
        let newHash = "";
        compare.hash.split("").forEach((v) => {
            if (localHash.includes(v)) {
                newHash += v;
            }
        });
        return new Support(this.log, newHash);
    }
    basicSupport() {
        var _a;
        if (adapter_1.device.platform === "ReactNative") {
            return true;
        }
        if ((0, mediasoup_client_1.detectDevice)() == null) {
            this.log.warn("support: mediasoups says device is unsupported", {
                ...adapter_1.device.browserInfo,
            });
            this.log.flush();
            return false;
        }
        if (!adapter_1.device.isImplements(feature_1.Feature.MEDIA_DEVICE)) {
            this.log.warn("support: no RTCPeerConnection", {
                ...adapter_1.device.browserInfo,
            });
            this.log.flush();
            return false;
        }
        if (adapter_1.device.isIosDevice && !adapter_1.device.isSafari) {
            if (adapter_1.device.isChrome) {
                if (!(0, user_agent_1.supportsModernChrome)(adapter_1.device)) {
                    this.log.warn("support: ios chrome support is only available on versions >= 14.3", {
                        ...adapter_1.device.browserInfo,
                    });
                }
                else {
                    return adapter_1.device.isImplements(feature_1.Feature.MEDIA_DEVICE);
                }
            }
            else {
                this.log.warn("support: non-safari ios", {
                    ...adapter_1.device.browserInfo,
                });
            }
            this.log.flush();
            return false;
        }
        if (adapter_1.device.isEdge && !adapter_1.device.isImplements(feature_1.Feature.WEBRTC)) {
            this.log.warn("support: msedge with missing webrtc api", {
                hasRTCRtpReceiver: (adapter_1.device === null || adapter_1.device === void 0 ? void 0 : adapter_1.device.RTCRtpReceiver) == null ? "false" : "true",
                hasRTCRtpSender: (adapter_1.device === null || adapter_1.device === void 0 ? void 0 : adapter_1.device.RTCRtpSender) == null ? "false" : "true",
                hasRTCRtpReceiverGetCapabilities: ((_a = adapter_1.device === null || adapter_1.device === void 0 ? void 0 : adapter_1.device.RTCRtpReceiver) === null || _a === void 0 ? void 0 : _a.getCapabilities) != null ? "true" : "false",
                ...adapter_1.device.browserInfo,
            });
            this.log.flush();
            return false;
        }
        return adapter_1.device.isImplements(feature_1.Feature.MEDIA_DEVICE);
    }
    buildLocalSupportHashSync() {
        if (!this.basicSupport()) {
            return "";
        }
        let localSupportHash = "";
        if (codecs.hasOpus()) {
            localSupportHash = localSupportHash.concat("o");
        }
        if (codecs.hasVP8()) {
            localSupportHash = localSupportHash.concat("v");
        }
        if (codecs.hasH264(this.log)) {
            localSupportHash = localSupportHash.concat("h");
        }
        this.log.setLoggerAggregate("supportHash", localSupportHash);
        this.log.debug("support: support hash generated", {
            ...adapter_1.device.browserInfo,
        });
        return localSupportHash;
    }
    async buildLocalSupportHashAsync() {
        if (!this.basicSupport()) {
            return "";
        }
        let localSupportHash = "";
        if (await codecs.hasOpusCapability(this.log)) {
            localSupportHash = localSupportHash.concat("o");
        }
        if (await codecs.hasVP8Capability(this.log)) {
            localSupportHash = localSupportHash.concat("v");
        }
        if (await codecs.hasH264Capability(this.log)) {
            localSupportHash = localSupportHash.concat("h");
        }
        if (localSupportHash !== "") {
            this.log.setLoggerAggregate("supportHash", localSupportHash);
        }
        this.log.debug("support: support hash generated", {
            ...adapter_1.device.browserInfo,
        });
        return localSupportHash;
    }
}
exports.Support = Support;
Support.displayName = "Support";
function videoSupported() {
    return adapter_1.device.isImplements(feature_1.Feature.MEDIA_DEVICE);
}
//# sourceMappingURL=index.js.map